import React, { PropsWithChildren, useState, useRef } from 'react'
import { FileUpload as FileUploadStyles } from '@airbus/elements/elements'
import { Button as ButtonStyles } from '@airbus/elements/elements'
import { SROnly as SROnlyStyles } from '@airbus/elements/elements'
import { Upload } from '@mui/icons-material'
import uniqueId from 'lodash.uniqueid'
import clsx from 'clsx'
import { createUseStyles } from '../../lib/styling'
import Typography from '../Typography/Typography'
import { CommonProps } from '../helperTypes'
import FileList from './FileList'

export type FileStatus = 'added' | 'uploading' | 'success' | 'error'

export type FileItem = {
  id: number
  data: File
  status: FileStatus
  percentage: number
  retry: boolean
  errorMsg: string
}

type BaseProps = PropsWithChildren<{
  /**
   * Comma seperated string of file types accepted.<br />
   * See [HTML attribute: accept](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/accept)
   */
  accept?: string
  /**
   * Drag and Drop variant
   * @default false
   */
  dragAndDrop?: boolean
  /**
   * Array of objects containing the state of the upload progress and percentage.<br />
   * structure: { id: number, data: file, retry: boolean, errorMsg: string, status: success | uploading | error | added, percentage: number }
   */
  files?: Array<FileItem>
  /**
   * The id property is use to set the id of the input element.
   * Useful for form management
   * @default unique generated id
   */
  id?: string
  /**
   * When the "multiple" prop is set to true, it supports uploading multiple files
   * @default false
   */
  multiple?: boolean
  /**
   * When new files are added, onAdd function is called with an array of new files as its parameter.
   * The developer is responsible for appending the new files to the list of existing files.
   */
  onAdd: (file: Array<FileItem>) => void
  /** When a file is deleted, onDelete function is called with the id of the deleted file as its parameter. */
  onDelete: (id: number) => void
  /** Called when user clicks on retry button, with the id of the file as its parameter */
  onRetry?: (id: number) => void
  /**
   * Maximum file size allowed in KB
   * @default no limit
   */
  size?: number
}>

export type FileUploadProps = BaseProps & CommonProps

const getFormattedSize = (size: number) => {
  if (!size) return ''
  if (size >= 1000) {
    return `${parseFloat((size / 1000).toFixed(2))} MB`
  } else {
    return `${parseFloat(size.toFixed(2))} KB`
  }
}

const useStyles = createUseStyles(FileUploadStyles, 'fileupload')
const useButtonStyles = createUseStyles(ButtonStyles, 'fileupload-btn')
const useSROnlyStyles = createUseStyles(SROnlyStyles, 'fileupload-sronly')

const FileUpload = ({
  accept = '*',
  className,
  dragAndDrop = false,
  files = [],
  id,
  multiple = false,
  onAdd,
  onDelete,
  onRetry,
  size,
  style,
  ...rest
}: FileUploadProps) => {
  const classes = useStyles()
  const buttonClasses = useButtonStyles()
  const srOnlyClasses = useSROnlyStyles()
  const [onDrag, setOnDrag] = useState(false)
  const inputFileRef = useRef<HTMLInputElement>(null)
  const idRef = useRef<string>(uniqueId('LabelId-'))

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setOnDrag(false)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setOnDrag(true)
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (inputFileRef && inputFileRef.current) {
      const { files } = e.target
      if (files && files.length) {
        handleFileUpload(files)
      }
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const files = e.dataTransfer.files
    if (dragAndDrop) {
      handleFileUpload(files)
    }
  }

  const handleFileUpload = (newFiles: FileList) => {
    let fileList: Array<FileItem> = []
    const newFileList = multiple ? newFiles : [newFiles[0]]
    let isAccepted = true
    let isExceeded = false
    for (let i = 0; i < newFileList.length; i++) {
      const file: FileItem = {
        id: Math.floor(1000 + Math.random() * 9000),
        data: newFileList[i],
        status: 'added',
        percentage: 0,
        retry: false,
        errorMsg: '',
      }
      isExceeded = false
      if (size) {
        isExceeded = newFileList[i].size > size * 1000 ? true : false
      }

      if (accept && accept !== '*') {
        isAccepted = accept
          .split(',')
          .some(
            (fileType) =>
              newFileList[i].type.includes(fileType.trim().replace(/[^a-zA-Z ]/g, '')) ||
              newFileList[i].type.includes(fileType.trim()),
          )
      }
      const hasError = !isAccepted || isExceeded
      if (hasError) {
        file.status = 'error'
        file.errorMsg = !isAccepted ? `${newFileList[i].type} is not allowed` : `File size exceeds ${size} KB`
        file.retry = false
      }
      multiple ? fileList.push(file) : (fileList = [file])
    }
    setOnDrag(false)
    onAdd(fileList)
    if (inputFileRef.current) {
      inputFileRef.current.value = ''
    }
  }

  const handleFileDelete = (fileId: number) => {
    // FIXME should we update inputFileRef.current.files also ?
    onDelete(fileId)
  }

  const supportedFilesInfos = React.useMemo(() => {
    const infos = []
    const hasAccept = accept !== '*'
    if (hasAccept) infos.push(`Files supported: ${accept}`)
    if (size) infos.push(`${hasAccept ? ' ' : ''}Max size: ${getFormattedSize(size)}`)
    return dragAndDrop ? infos : infos.join(' - ')
  }, [accept, dragAndDrop, size])

  // TODO provide props to customize default mesasge ?
  const renderPlaceholder = () => (
    <>
      <div className={classes.placeholder} />
      <div className={classes.message}>
        {!onDrag && `Drag and `}Drop file{multiple && 's'} here
      </div>
    </>
  )

  const renderButton = (buttonVariant: 'primary' | 'ghost') => {
    var labelId = id || idRef.current
    return (
      <div>
        <label
          htmlFor={`${labelId}-label`}
          className={clsx(className, classes.browse, buttonClasses.root, buttonClasses[`_${buttonVariant}`])}
          style={style}
        >
          <span role='button'>
            <Upload /> {multiple ? 'Browse Files' : 'Browse File'}
          </span>
        </label>
        <input
          className={srOnlyClasses.root}
          id={`${labelId}-label`}
          ref={inputFileRef}
          onChange={(e) => handleFileInput(e)}
          type='file'
          multiple={multiple}
          accept={accept}
        />
      </div>
    )
  }

  return (
    <>
      {!dragAndDrop && (
        <div {...rest}>
          <Typography variant='small'>{supportedFilesInfos}</Typography>
          {renderButton('primary')}
        </div>
      )}
      {dragAndDrop && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={classes.root}
          {...rest}
        >
          {onDrag && <div className={classes.overlay}>{renderPlaceholder()}</div>}
          {renderPlaceholder()}
          {(supportedFilesInfos[0] || supportedFilesInfos[1]) && (
            <div className={classes.supportedFiles}>
              {supportedFilesInfos[0] && <span>{supportedFilesInfos[0]}</span>}
              {supportedFilesInfos[1] && <span>{supportedFilesInfos[1]}</span>}
            </div>
          )}
          {renderButton('ghost')}
        </div>
      )}
      {files.length > 0 && <FileList onDelete={handleFileDelete} onRetry={onRetry} files={files} />}
    </>
  )
}

export default FileUpload
